import React, { useEffect, FC, ReactNode } from 'react';
import {
  useReactTable,
  getExpandedRowModel,
  getGroupedRowModel,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  GroupingState,
  SortingState,
  Row,
  TableOptions,
  PaginationState,
  getSortedRowModel,
  RowData,
  Table,
  VisibilityState,
  ExpandedState,
  RowSelectionState,
  functionalUpdate,
} from '@tanstack/react-table';
import { TableHeadingCell } from './TableHeadingCell';
import { TableCell } from './TableCell';
import { TableHeadingRowWrapper } from './TableHeadingRowWrapper';
import { TableRowWrapper } from './TableRowWrapper';
import { TableHeadWrapper } from './TableHeadWrapper';
import { TableBodyWrapper } from './TableBodyWrapper';
import { TableWrapper } from './TableWrapper';
import { TableSubComponentWrapper } from './TableSubComponentWrapper';
import { Pagination } from '../pagination';
import { ColumnVisibility } from './ColumnVisibility';
import { TableCheckBox } from './TableCheckbox';

import { useTranslation } from 'react-i18next';

const always = () => true;

export type TableV2Props<T, R> = {
  subComponentProps?: R;
  subComponent?: FC<
    {
      row?: Row<T>;
      groupedBy?: string[];
    } & R
  >;
  tableHeader?: ReactNode;
  pagination?: PaginationState & {
    pageCount: number;
    setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  };
  visibility?: {
    columnVisibility: VisibilityState;
    setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>;
  };
  sorting?: {
    sortBy: SortingState;
    setSortBy: React.Dispatch<React.SetStateAction<SortingState>>;
  };
  grouping?: {
    groupBy: string[];
    setGroupBy: React.Dispatch<React.SetStateAction<string[]>>;
  };
  defaultHidden?: string[];
  total?: number;
  enablePagination?: boolean;
  border?: boolean;
  tableHeadingRender?: React.FC<TableHeadingRenderProps<T>>;
  loading?: ReactNode;
  error?: ReactNode;
  enableRowClickable?: boolean;
  enableMultiRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  onClickRow?: (value: Row<T>) => void;
  disableRow?: (value: Row<T>, test?: string) => boolean;
  'data-testid'?: string;
};
export type TablePaginationState = PaginationState;
export type TableSortingState = SortingState;
export type TableVisibilityState = VisibilityState;
export type TableHeadingRenderProps<T> = {
  columnVisibility?: ReactNode;
  table?: Table<T>;
};

export function TableV2<
  T extends RowData,
  R extends { [key: string]: unknown },
>({
  columns,
  sorting,
  data,
  defaultColumn,
  subComponent: SubComponent,
  pagination,
  grouping,
  defaultHidden,
  tableHeader,
  tableHeadingRender: TableHeadingRender,
  subComponentProps,
  enablePagination = true,
  manualExpanding,
  visibility,
  error,
  loading,
  border = true,
  total,
  enableRowClickable = false,
  enableMultiRowSelection = true,
  disableRow,
  onClickRow,
  rowSelection,
  setRowSelection,
  'data-testid': dataTestId,
  ...tableProps
}: TableV2Props<T, R> &
  Omit<
    TableOptions<T>,
    'getCoreRowModel' | 'pageCount' | 'manualPagination' | 'manualGrouping'
  >) {
  const { t } = useTranslation('designSystem');
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const [groupingData, setGrouping] = React.useState<GroupingState>(
    grouping?.groupBy || [],
  );
  const [sortingData, setSorting] = React.useState<SortingState>([]);

  const [columnVisibilityState, setColumnVisibility] = React.useState(() =>
    defaultHidden
      ? defaultHidden.reduce((acc, col) => ({ ...acc, [col]: false }), {})
      : {},
  );
  const groupLength = groupingData.length;
  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>(
      pagination ?? {
        pageIndex: 0,
        pageSize: 10,
      },
    );
  const paginationData = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const manualGrouping = !!grouping;
  const manualPagination = !!pagination;
  const manualSorting = !!sorting;
  const manualColumn = !!visibility;
  const setGroupBy = grouping?.setGroupBy;

  useEffect(() => {
    if (manualGrouping && setGroupBy) {
      if (groupingData.length > 1) {
        const elements = [...groupingData];
        const lastElem = elements.pop();
        setGroupBy(lastElem ? [lastElem] : []);
        setGrouping(lastElem ? [lastElem] : []);
      } else {
        setGroupBy(groupingData);
      }
    }
  }, [groupLength, manualGrouping, setGroupBy, groupingData]);

  if (setRowSelection) {
    columns.unshift({
      accessorKey: 'select',
      header: '',
      cell: ({ row }: any) => (
        <TableCheckBox
          className="cursor-pointer"
          enableMultiRowSelection={enableMultiRowSelection}
          {...{
            checked: row.getIsSelected(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      ),
      maxSize: 50,
    });
  }

  const manualExpandFunc = tableProps.getRowCanExpand || always;
  const table = useReactTable<T>({
    data,
    columns,
    onExpandedChange: setExpanded,
    state: {
      expanded,
      grouping: manualGrouping ? grouping.groupBy : groupingData,
      pagination: manualPagination ? pagination : paginationData,
      sorting: manualSorting ? sorting.sortBy : sortingData,
      columnVisibility: manualColumn
        ? visibility.columnVisibility
        : columnVisibilityState,
      rowSelection: rowSelection || {},
    },
    onColumnVisibilityChange: (value: any) => {
      setColumnVisibility(value);
      visibility?.setColumnVisibility?.(value);
    },
    onGroupingChange: (value: any) => {
      setGrouping(value);
    },
    onPaginationChange: (value: any) => {
      // TanStack queues a page-index reset on every data change. Returning the
      // previous state when the page has not moved lets React bail out, so the
      // reset cannot re-render and re-trigger itself.
      setPagination((previous) => {
        const next = functionalUpdate(value, previous);
        return next.pageIndex === previous.pageIndex &&
          next.pageSize === previous.pageSize
          ? previous
          : next;
      });
      pagination?.setPagination?.(value);
    },
    onSortingChange: (value: any) => {
      setSorting(value);
      sorting?.setSortBy?.(value);
    },
    getRowCanExpand: manualExpanding ? manualExpandFunc : undefined,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    enableMultiRowSelection: enableMultiRowSelection,
    onRowSelectionChange: setRowSelection || undefined,
    ...tableProps,
    defaultColumn: {
      enableGrouping: false,
      enableSorting: false,
      enableResizing: true,
      ...defaultColumn,
    },
    manualPagination,
    manualSorting,
    manualExpanding: !!SubComponent,
    pageCount: pagination?.pageCount,
  });
  return (
    <div className={`flex flex-col gap-4`} data-testid={dataTestId}>
      {TableHeadingRender ? (
        <TableHeadingRender
          columnVisibility={<ColumnVisibility table={table} />}
          table={table}
        />
      ) : (
        (tableHeader || tableProps.enableHiding) && (
          <div className="flex justify-between gap-4">
            {tableHeader}
            {tableProps.enableHiding && (
              <div className="mt-2 ml-auto">
                <ColumnVisibility table={table} />
              </div>
            )}
          </div>
        )
      )}
      <div
        className={`${
          border ? 'rounded-card border-container-border border' : ''
        } ${
          // The frame draws the bottom edge, so the last row needs no divider;
          // the pagination bar draws its own top edge when it shows.
          border ? '[&_tbody>tr:last-child]:border-b-0' : ''
        } flex flex-col overflow-hidden`}
      >
        <div className="min-w-full overflow-x-auto align-middle">
          <TableWrapper>
            <TableHeadWrapper>
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  <TableHeadingRowWrapper>
                    {headerGroup.headers.map((header) => {
                      return <TableHeadingCell key={header.id} {...header} />;
                    })}
                  </TableHeadingRowWrapper>
                </React.Fragment>
              ))}
            </TableHeadWrapper>
            <TableBodyWrapper>
              {loading && (
                <tr>
                  <td colSpan={columns.length}>{loading}</td>
                </tr>
              )}
              {error && (
                <tr>
                  <td colSpan={columns.length}>{error}</td>
                </tr>
              )}
              {!loading && !error && table.getRowModel().rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="p-5 text-center">
                    {t('noDataFound')}
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                table.getRowModel().rows.map((row) => {
                  return (
                    <React.Fragment key={row.id}>
                      <TableRowWrapper
                        enableRowClickable={enableRowClickable}
                        onClickRow={onClickRow}
                        index={row.index}
                        row={row}
                        disableRow={disableRow}
                      >
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <TableCell
                              key={cell.id}
                              cell={cell}
                              row={row}
                              enableRowClickable={enableRowClickable}
                            />
                          );
                        })}
                      </TableRowWrapper>
                      {SubComponent && row.getIsExpanded() && (
                        <TableSubComponentWrapper
                          colSpan={row.getVisibleCells().length}
                        >
                          <SubComponent
                            row={row}
                            groupedBy={grouping?.groupBy}
                            {...(subComponentProps as any)}
                          />
                        </TableSubComponentWrapper>
                      )}
                    </React.Fragment>
                  );
                })}
            </TableBodyWrapper>
          </TableWrapper>
        </div>
        {enablePagination && (
          <Pagination
            showTotal={
              manualGrouping ||
              !table.getGroupedRowModel().rows.some((x) => x.getIsGrouped())
            }
            wrapperClassName={`py-3 bg-white dark:bg-gray-800 ${border ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}
            perPage={table.getState().pagination.pageSize}
            setPerPage={table.setPageSize}
            page={table.getState().pagination.pageIndex}
            setPage={table.setPageIndex}
            pageCount={table.getPageCount()}
            total={total || data.length}
          />
        )}
      </div>
    </div>
  );
}
